import { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Link,
  Pagination,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useQuery } from "@tanstack/react-query";
import axios from "../utils/axios-instance";
import type { AppPagination } from "../types";

type SentMiniAppNotification = {
  title: string;
  message: string;
  createdAt: string;
  recipientCount: number;
  attachmentType: "none" | "image" | "video" | "file";
  attachmentUrl: string | null;
  attachments?: Array<{
    attachmentType: "image" | "video" | "file";
    attachmentUrl: string;
  }>;
  targetAllUsers: boolean;
  targetUserIds: number[];
};

const PAGE_SIZE = 20;

const fetchSentNotifications = async (
  page: number,
  search: string,
): Promise<AppPagination<SentMiniAppNotification>> => {
  const query = new URLSearchParams({
    page: String(page),
    limit: String(PAGE_SIZE),
  });
  if (search.trim()) {
    query.set("search", search.trim());
  }

  const response = await axios.get<AppPagination<SentMiniAppNotification>>(
    `/admin/miniapp-notifications/sent?${query.toString()}`,
  );
  return response.data;
};

export function MiniAppNotificationsSection() {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");

  const debouncedSearch = useMemo(() => searchInput, [searchInput]);

  const { data, isLoading, isError, error } = useQuery<
    AppPagination<SentMiniAppNotification>,
    Error
  >({
    queryKey: ["miniapp-notifications-sent", page, debouncedSearch],
    queryFn: () => fetchSentNotifications(page, debouncedSearch),
  });

  const items = data?.items ?? [];
  const totalPages = data?.meta?.totalPages ?? 1;
  const getAttachmentList = (item: SentMiniAppNotification) => {
    if (Array.isArray(item.attachments) && item.attachments.length > 0) {
      return item.attachments;
    }
    if (item.attachmentUrl && item.attachmentType !== "none") {
      return [
        {
          attachmentType: item.attachmentType,
          attachmentUrl: item.attachmentUrl,
        },
      ];
    }
    return [];
  };

  return (
    <Card
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 2,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        background: "white",
      }}
    >
      <Box display="flex" flexDirection="column" gap={2}>
        <Typography variant="h5" fontWeight="bold" color="text.secondary">
          Sent Mini App Notifications
        </Typography>

        <TextField
          size="small"
          fullWidth
          label="Search"
          placeholder="Search by title or message"
          value={searchInput}
          onChange={(event) => {
            setSearchInput(event.target.value);
            setPage(1);
          }}
        />

        {isError ? (
          <Alert severity="error">
            {error.message || "Failed to load notifications."}
          </Alert>
        ) : isLoading ? (
          <Stack spacing={1.5}>
            {[...Array(4)].map((_, idx) => (
              <Skeleton key={idx} variant="rounded" height={88} />
            ))}
          </Stack>
        ) : items.length === 0 ? (
          <Typography color="text.secondary">
            No sent notifications found.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {items.map((item, idx) => (
              <Card
                key={`${item.title}-${item.createdAt}-${idx}`}
                variant="outlined"
              >
                <CardContent>
                  <Typography fontWeight={700}>
                    {item.title || "Untitled"}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
                  >
                    {item.message || "—"}
                  </Typography>
                  {getAttachmentList(item).map(
                    (attachment, attachmentIndex) => (
                      <Box
                        mt={1.5}
                        key={`${attachment.attachmentUrl}-${attachmentIndex}`}
                      >
                        {attachment.attachmentType === "image" && (
                          <Box
                            component="img"
                            src={attachment.attachmentUrl}
                            alt={item.title || "Notification image"}
                            sx={{
                              width: "100%",
                              maxWidth: 320,
                              borderRadius: 1.5,
                              border: "1px solid #e0e0e0",
                              objectFit: "cover",
                              maxHeight: 180,
                            }}
                          />
                        )}
                        {attachment.attachmentType === "video" && (
                          <Box
                            component="video"
                            controls
                            src={attachment.attachmentUrl}
                            sx={{
                              width: "100%",
                              maxWidth: 360,
                              borderRadius: 1.5,
                              border: "1px solid #e0e0e0",
                              maxHeight: 220,
                            }}
                          />
                        )}
                        {attachment.attachmentType === "file" && (
                          <Link
                            href={attachment.attachmentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Open attachment{" "}
                            {getAttachmentList(item).length > 1
                              ? `#${attachmentIndex + 1}`
                              : ""}
                          </Link>
                        )}
                      </Box>
                    ),
                  )}
                  <Box
                    mt={1}
                    display="flex"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap={1}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Recipients: {item.recipientCount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Attachment: {getAttachmentList(item).length || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.createdAt
                        ? new Date(item.createdAt).toLocaleString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "—"}
                    </Typography>
                  </Box>
                  <Box mt={1.25}>
                    {item.targetAllUsers ? (
                      <Chip
                        size="small"
                        color="primary"
                        label="Target: All users"
                      />
                    ) : (
                      <Accordion
                        disableGutters
                        elevation={0}
                        sx={{ border: "1px solid #e0e0e0", borderRadius: 1 }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="body2" fontWeight={600}>
                            Target users ({item.targetUserIds?.length || 0})
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          {item.targetUserIds?.length ? (
                            <Box
                              sx={{
                                maxHeight: 120,
                                overflowY: "auto",
                                p: 1,
                                borderRadius: 1,
                                backgroundColor: "#fafafa",
                                border: "1px dashed #d0d0d0",
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ wordBreak: "break-word" }}
                              >
                                {item.targetUserIds.join(", ")}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              No user IDs stored.
                            </Typography>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}

        <Box display="flex" justifyContent="center" mt={1}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            size="large"
          />
        </Box>
      </Box>
    </Card>
  );
}
